import { useMemo } from "react";
import type { ColumnDef } from "../../components/base-table-layout";
import { createEhrWriteBackColumn } from "../../ehr/create-ehr-column";
import { EhrContextValue } from "../../../shared-logic/ehr-context/ehr-context";
import { createDiagnosticReportFilters } from "../../utils/diagnostic-report-filters";
import { createNoteMatchData } from "../../utils/note-processing";
import { TableRow } from "../../utils/tables-metadata";
import { DiagnosticReportRowData } from "./diagnostic-reports-rows";

/**
 * Creates column definitions for notes tables.
 * Optionally includes EHR write-back column if context is available.
 */
export function useNotesColumns(ehrContext: EhrContextValue) {
  return useMemo(() => {
    const cols: ColumnDef<DiagnosticReportRowData>[] = [
      { key: "typeOfReport", header: "Note Type" },
      { key: "noteCategory", header: "Category" },
      { key: "specialty", header: "Specialty" },
      { key: "reasonForVisit", header: "Reason For Visit" },
      { key: "location", header: "Location" },
      { key: "date", header: "Date", maxWidth: "150px", sort: "desc" },
    ];

    const ehrColumn = createEhrWriteBackColumn<DiagnosticReportRowData>(ehrContext, "note");
    if (ehrColumn) cols.push(ehrColumn);

    return cols;
  }, [ehrContext]);
}

export type NotesFilterOption = {
  label: string;
  groupLabel: string;
  isChecked: boolean;
  filterFn: (rowData: DiagnosticReportRowData) => boolean;
};

/**
 * Creates filter options for notes tables based on row data.
 * Filters by category and specialty.
 */
export function useNotesFilterOptions(rows: TableRow[]): NotesFilterOption[] {
  return useMemo(() => {
    const diagnosticRows = rows as DiagnosticReportRowData[];
    const filters = createDiagnosticReportFilters(diagnosticRows);
    return filters.map(option => ({
      label: option.label,
      groupLabel: option.groupLabel,
      isChecked: true,
      filterFn: (rowData: DiagnosticReportRowData) => {
        const [type, value] = option.value.split(":");
        switch (type) {
          case "category":
            if (value === "undefined") {
              return !rowData.noteCategory || rowData.noteCategory === "-";
            }
            return rowData.noteCategory === value;
          case "specialty":
            if (value === "undefined") {
              return !rowData.specialty || rowData.specialty === "-";
            }
            return rowData.specialty === value;
          default:
            return true;
        }
      },
    }));
  }, [rows]);
}

/**
 * Checks if a row has note content matches for the given search query.
 */
export function hasNoteMatches(row: TableRow, filterQuery: string): boolean {
  const noteData = row.noteData as { fullText: string } | undefined;
  if (!noteData?.fullText || !filterQuery.trim()) return false;
  const matchData = createNoteMatchData(noteData.fullText, filterQuery);
  return Boolean(matchData && matchData.count > 0);
}
